<template>
  <div class="aroma-product-detail">
    <aroma-secondary-nav title="纯植物精油复配芳香液" fallback="AromaShop" />
    <main class="aroma-product-detail__scroll">
      <div v-if="loading" class="aroma-page-state">
        <van-loading size="28px">加载中...</van-loading>
      </div>
      <van-empty v-else-if="errorMessage" :description="errorMessage">
        <van-button type="primary" size="small" @click="loadProduct">重新加载</van-button>
      </van-empty>
      <template v-else-if="product">
        <section class="aroma-product-detail__summary">
          <div class="aroma-product-detail__summary-content">
            <h1>{{ product.packageName || '芳香液' }}</h1>
            <p>{{ product.description }}</p>
            <div v-if="labels.length" class="aroma-product-detail__tags">
              <span v-for="item in labels" :key="item">{{ item }}</span>
            </div>
          </div>
        </section>

        <nav class="aroma-product-detail__tabs" aria-label="商品详情导航">
          <button
            v-for="(item, index) in tabs"
            :key="item"
            type="button"
            :class="{ 'is-active': activeTab === index }"
            @click="scrollToSection(index)"
          >{{ item }}</button>
        </nav>

        <section ref="introduction" class="aroma-product-detail__section">
          <h2>服务介绍</h2>
          <div v-if="imageFailed" class="aroma-product-detail__image-error">
            <van-icon name="photo-fail" size="40" />
            <p>商品详情加载失败</p>
            <van-button plain type="primary" size="small" @click="retryImage">重新加载</van-button>
          </div>
          <img
            v-else
            :key="imageRetryKey"
            class="aroma-product-detail__image"
            :src="detailImage"
            alt="芳香液商品详情"
            @error="imageFailed = true"
          />
        </section>

        <section ref="description" class="aroma-product-detail__section is-description">
          <h2>服务说明</h2>
          <div class="aroma-product-detail__description">
            <p v-for="(item, index) in serviceDescriptionLines" :key="index">{{ item }}</p>
          </div>
        </section>
        <p class="aroma-product-detail__record">备案号：浙ICP备2023008082号-1</p>
      </template>
    </main>

    <footer v-if="product" class="aroma-bottom-action">
      <div class="aroma-bottom-action__price">
        <span>体验价：</span>
        <strong>￥{{ price }}</strong>
      </div>
      <van-button
        round
        type="primary"
        :disabled="!task"
        @click="buy"
      >立即购买</van-button>
    </footer>
  </div>
</template>

<script>
import API from '@/api'
import AromaSecondaryNav from '@/components/aroma-report/AromaSecondaryNav.vue'
import { AROMA_DETAIL_IMAGE, AROMA_PACKAGE_ID, AROMA_TASK_ID, formatPrice } from '@/utils/aroma-shop'
import { WECHAT_APP_ID, buildWechatAuthorizeUrl, isWeixinBrowser } from '@/utils/aroma-pay'
import { clearSniSession, getSniSession, saveSniSession } from '@/utils/sni-session'

export default {
  name: 'AromaProductDetail',

  components: {
    AromaSecondaryNav
  },

  data () {
    return {
      loading: false,
      product: null,
      task: null,
      errorMessage: '',
      imageFailed: false,
      imageRetryKey: 0,
      activeTab: 0,
      tabs: ['服务介绍', '服务说明']
    }
  },

  computed: {
    sn () {
      return this.$route.query.sn || ''
    },

    detailImage () {
      return AROMA_DETAIL_IMAGE
    },

    price () {
      return formatPrice(this.product?.packagePrice)
    },

    labels () {
      return (this.product?.label || '').split(',').map(item => item.trim()).filter(Boolean)
    },

    serviceDescriptionLines () {
      return (this.product?.serviceDescription || '').split('\n').map(item => item.trim()).filter(Boolean)
    },

    session () {
      return getSniSession(this.sn)
    }
  },

  mounted () {
    this.loadProduct()
  },

  methods: {
    async loadProduct () {
      this.loading = true
      this.errorMessage = ''
      try {
        const [packageResponse, taskResponse, profile] = await Promise.all([
          API.aromaPackageDetail(AROMA_PACKAGE_ID),
          API.aromaOrderTask(AROMA_TASK_ID),
          API.sniCurrent(this.session.token)
        ])
        if (packageResponse?.code !== '1' || !packageResponse.data) {
          throw new Error(packageResponse?.msg || '商品暂不可用')
        }
        if (taskResponse?.code !== '1' || !taskResponse.data) {
          throw new Error(taskResponse?.msg || '商品暂不可用')
        }
        this.product = packageResponse.data
        this.task = taskResponse.data
        saveSniSession(this.sn, {
          ...this.session,
          profileCompleted: profile.profileCompleted
        })
      } catch (error) {
        if (error?.code === '8' || error?.code === '11') {
          clearSniSession(this.sn)
          this.$router.replace({
            name: 'AromaReportLogin',
            query: { sn: this.sn, redirect: this.$route.fullPath }
          })
          return
        }
        this.errorMessage = error?.message || '商品暂不可用'
      } finally {
        this.loading = false
      }
    },

    retryImage () {
      this.imageFailed = false
      this.imageRetryKey += 1
    },

    scrollToSection (index) {
      this.activeTab = index
      const target = index === 0 ? this.$refs.introduction : this.$refs.description
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },

    buy () {
      const checkout = this.$router.resolve({ name: 'AromaCheckout', query: { sn: this.sn } })
      if (isWeixinBrowser(window.navigator.userAgent)) {
        const redirectUri = new URL(checkout.href, window.location.origin).toString()
        window.location.assign(buildWechatAuthorizeUrl({
          appId: WECHAT_APP_ID,
          redirectUri,
          state: 'aroma-checkout'
        }))
        return
      }
      this.$router.push({ name: 'AromaCheckout', query: { sn: this.sn } })
    }
  }
}
</script>

<style scoped lang="scss">
@import '@/assets/css/aroma-commerce.scss';

.aroma-product-detail {
  min-height: 100%;
  background: #fff;

  &__scroll {
    min-height: 100%;
    padding-bottom: calc(82px + env(safe-area-inset-bottom));
    box-sizing: border-box;
  }

  &__summary {
    display: flex;
    min-height: 94px;
    padding: 10px 15px 17px;
    box-sizing: border-box;
  }

  &__icon {
    display: flex;
    width: 60px;
    height: 60px;
    flex: 0 0 60px;
    align-items: center;
    justify-content: center;
    margin-top: 6px;
    color: #fff;
    font-size: 20px;
    font-weight: 500;
    background: linear-gradient(180deg, #596afd 0%, #b0b9ff 100%);
    border-radius: 10px;
  }

  &__summary-content {
    min-width: 0;
    flex: 1;

    h1 {
      overflow: hidden;
      color: #000;
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    p {
      display: -webkit-box;
      margin-top: 5px;
      overflow: hidden;
      color: #afafaf;
      font-size: 12px;
      line-height: 17px;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
  }

  &__tags {
    display: flex;
    flex-wrap: wrap;
    gap: 5px;
    margin-top: 5px;

    span {
      min-height: 20px;
      padding: 2px 6px;
      color: #afafaf;
      font-size: 10px;
      line-height: 14px;
      background: rgba(0, 0, 0, 0.04);
      border: 1px solid rgba(0, 0, 0, 0.15);
      border-radius: 3px;
      box-sizing: border-box;
    }
  }

  &__tabs {
    position: sticky;
    z-index: 5;
    top: 0;
    display: grid;
    height: 44px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    background: #fff;
    border-bottom: 1px solid #f2f2f2;

    button {
      position: relative;
      color: #606266;
      font-size: 14px;
      background: #fff;
      border: 0;

      &.is-active {
        color: #596afd;
        font-weight: 500;
      }

      &.is-active::after {
        position: absolute;
        right: 50%;
        bottom: 0;
        width: 56px;
        height: 2px;
        background: #596afd;
        content: '';
        transform: translateX(50%);
      }
    }
  }

  &__section {
    padding: 0 10px;
    scroll-margin-top: 44px;

    h2 {
      padding: 15px 0;
      color: #000;
      font-size: 16px;
      font-weight: 500;
      line-height: 22px;
      text-align: center;
    }

    &.is-description {
      padding: 0 15px;
    }
  }

  &__image {
    display: block;
    width: 100%;
    height: auto;
  }

  &__image-error {
    display: flex;
    min-height: 280px;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    color: #8a8f98;

    p {
      margin: 12px 0 18px;
      font-size: 14px;
      line-height: 22px;
    }
  }

  &__description {
    padding: 15px;
    color: #333;
    font-size: 14px;
    line-height: 24px;
    background: #fff;
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(182, 182, 182, 0.5);

    p + p {
      margin-top: 8px;
    }
  }

  &__record {
    padding: 44px 12px 25px;
    color: #666;
    font-size: 12px;
    line-height: 18px;
    text-align: center;
  }
}
</style>
